import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

// Mobile browsers (iPadOS/iOS Safari in particular) only shrink the *visual*
// viewport when the software keyboard opens — the layout viewport keeps its
// size, so composers pinned to bottom:0 or vertically centered end up hidden
// behind the keyboard. Measure the occluded strip so screens can lift the
// composer on web the same way native keyboard events do. Browsers that
// instead resize the layout viewport (e.g. interactive-widget=resizes-content)
// report a ~0 gap, which correctly reads as "no lift needed".
// Same heuristic ModalRoot uses for mobile-web modals.
const KEYBOARD_OPEN_THRESHOLD_PX = 100; // ignore URL-bar / toolbar show-hide jitter

const hasEditableFocus = (documentObject) => {
  const activeElement = documentObject?.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName?.toUpperCase();
  return tagName === 'INPUT'
    || tagName === 'TEXTAREA'
    || activeElement.isContentEditable === true;
};

export const readOccludedHeight = (windowObject, documentObject) => {
  const vv = windowObject?.visualViewport;
  if (!vv || !hasEditableFocus(documentObject)) return 0;

  const occluded = windowObject.innerHeight - vv.height - vv.offsetTop;
  return occluded > KEYBOARD_OPEN_THRESHOLD_PX ? Math.round(occluded) : 0;
};

export default function useWebKeyboardHeight() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const onViewportChange = () => {
      // Android Chrome changes visualViewport while its URL bar/toolbars
      // collapse during an ordinary page scroll. Only treat the occluded strip
      // as a keyboard while an editable control actually owns focus; otherwise
      // moving browser chrome would shift the composer and list padding during
      // the user's gesture.
      setKeyboardHeight(readOccludedHeight(window, document));
    };
    const onFocusOut = () => setKeyboardHeight(0);
    onViewportChange();
    vv.addEventListener('resize', onViewportChange);
    vv.addEventListener('scroll', onViewportChange);
    // innerHeight is the other half of the measurement. Android browsers can
    // update the layout viewport after the visual viewport has already fired.
    window.addEventListener('resize', onViewportChange);
    document.addEventListener('focusin', onViewportChange);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      vv.removeEventListener('resize', onViewportChange);
      vv.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
      document.removeEventListener('focusin', onViewportChange);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  return Platform.OS === 'web' ? keyboardHeight : 0;
}
